import React, { useCallback, useState } from 'react';

import { MessageBox } from '@/components/generic/MessageBox';
import { Pill } from '@/components/generic/Pill';
import { Divider } from '@/components/shared/Divider';

import { YesNoQuestion } from './components/YesNoQuestion';
import { initialQuestions } from './constants';

import styles from './EligibilityCheck.module.scss';

type Props = {
  updateOrderByField: (field: string, value: unknown) => void;
};

export const EligibilityCheck = ({ updateOrderByField }: Props) => {
  const [questions, setQuestions] = useState(initialQuestions);

  const setAnswer = useCallback(
    index => value => {
      setQuestions(prevQuestions => {
        return prevQuestions.map((question, i) =>
          i === index ? { ...question, answer: value } : question,
        );
      });
      updateOrderByField(questions[index].fieldName, value);
    },
    [questions, setQuestions, updateOrderByField],
  );

  return (
    <div>
      <MessageBox title="Revised Attic & Roof Deck Requirements" variant="warning">
        <p>
          We’ve updated the roof condition requirements for Timberline Solar installations, which
          better ensures optimal system performance.
        </p>
        <p>New installations MUST have:</p>
        <ul className={styles.requirement_list}>
          <li>An attic that is vented and not sealed</li>
          <li>
            No spray foam, radiant barriers, or insulation exceeding R-13 on the underside of the
            roof deck
          </li>
          <li>Solid roof decking (no skip sheathing or wood planking) – plywood or OSB</li>
        </ul>
        <p>
          Roofs planes that cannot meet the above requirements are ineligible for Timberline Solar
          (until and unless they receive additional upgrades and services to bring their roof into
          compliance).
        </p>
      </MessageBox>

      <div className={styles.questions}>
        {questions.map(({ answer, fieldName, question, warning }, index) => (
          <div key={fieldName}>
            <YesNoQuestion
              answer={answer}
              key={fieldName}
              question={question}
              setAnswer={setAnswer(index)}
            />
            {answer && (
              <Pill
                className={styles.warning_message}
                key={`${fieldName}-warning`}
                showIcon
                variant="warning"
              >
                {warning}
              </Pill>
            )}
            {index !== questions.length - 1 && (
              <Divider className={styles.divider} gutters="medium" key={`${fieldName}-divider`} />
            )}
          </div>
        ))}
      </div>
    </div>
  );
};
